import React from "react";

import "assets/css/custom.css";
import "assets/scss/material-dashboard-pro-react.scss?v=1.9.0";
import "react-perfect-scrollbar/dist/css/styles.css";

import { createRoot } from "react-dom/client";

import { CookiesProvider } from "react-cookie";

import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { ThemeProvider as JssThemeProvider } from "react-jss";

import { createBrowserHistory } from "history";

import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";

import { theme } from "themeContext.js";
import RouteChangeTracker from "analytics/RouteChangeTracker";

import AuthLayout from "layouts/AuthLayout.js";
import JournalLayout from "layouts/JournalLayout.js";
import PricingLayout from "layouts/PricingLayout.js";
import CustomerLayout from "layouts/CustomerLayout.js";
import LoadingLogo from "components/Loading/LoadingLogo";
import ErrorBoundary from "ErrorBoundary";
import PrivacyPolicy from "views/Legal/PrivacyPolicy";
import CookiePolicy from "views/Legal/CookiePolicy";
import ContactLayout from "layouts/ContactLayout";
import AboutLayout from "layouts/AboutLayout";

import NewsDialog from "components/Sales/NewsDialog";

import { UserProvider } from "services/auth";

import Snackbar from "@mui/material/Snackbar";
import Slide from "@mui/material/Slide";

const AdminLayout = React.lazy(() => import("layouts/AdminLayout.js"));
const LegalLayout = React.lazy(() => import("layouts/LegalLayout.js"));

const hist = createBrowserHistory();

if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
  // dev code
} else {
  // prod code
  if (!window.console) window.console = {};
  var methods = ["log", "debug", "warn", "info"];
  for (var i = 0; i < methods.length; i++) {
    console[methods[i]] = function () {};
  }
}

const App = () => {
  const [masterAlert, setMasterAlert] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({ show: false, message: "" });

  // Handle exiting checkout without subscribing
  React.useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    const checkout = query.get("checkout");
    if (checkout === "success") {
      setSnackbar({
        show: true,
        message:
          "Subscribed!  It might take a moment for your account changes to take place.",
      });
    }
  }, []);

  const closeSnakbar = () => {
    setSnackbar({ show: false, message: "" });
  };

  const TransitionDown = (props) => {
    return <Slide {...props} direction="down" />;
  };

  return (
    <StyledEngineProvider injectFirst>
      <NewsDialog />
      <ThemeProvider theme={theme}>
        <JssThemeProvider theme={theme}>
          {masterAlert}

          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={snackbar.show}
            onClose={closeSnakbar}
            message={snackbar.message}
            TransitionComponent={TransitionDown}
            style={{ pointerEvents: "none" }}
          />

          <BrowserRouter navigator={hist}>
            <UserProvider>
              <RouteChangeTracker />
              <Routes>
                <Route
                  path="/*"
                  element={<AuthLayout setMasterAlert={setMasterAlert} />}
                />
                <Route
                  path="/admin/*"
                  element={
                    <React.Suspense fallback={<LoadingLogo />}>
                      <AdminLayout
                        setMasterAlert={setMasterAlert}
                        setSnackbar={setSnackbar}
                      />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/journal/*"
                  element={
                    <React.Suspense fallback={<LoadingLogo />}>
                      <JournalLayout />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/plans/*"
                  element={
                    <React.Suspense fallback={<LoadingLogo />}>
                      <PricingLayout />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/contact-us/*"
                  element={
                    <React.Suspense fallback={<LoadingLogo />}>
                      <ContactLayout />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/about/*"
                  element={
                    <React.Suspense fallback={<LoadingLogo />}>
                      <AboutLayout />
                    </React.Suspense>
                  }
                />
                <Route
                  path="/legal/*"
                  element={
                    <React.Suspense fallback={<LoadingLogo />}>
                      <LegalLayout />
                    </React.Suspense>
                  }
                >
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="cookie-policy" element={<CookiePolicy />} />
                </Route>
                <Route
                  path="/customer/*"
                  element={
                    <React.Suspense fallback={<LoadingLogo />}>
                      <CustomerLayout setSnackbar={setSnackbar} />
                    </React.Suspense>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </UserProvider>
          </BrowserRouter>
        </JssThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <CookiesProvider>
        <App />
      </CookiesProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
